/* eslint-disable react-refresh/only-export-components */
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { useAuth } from "./hooks/useAuth";
import { AuthProvider } from "./hooks/AuthProvider";
import "./index.css";
import { Providers } from "./providers";

const initialAuthContext = {
  isAuthenticated: false,
  isInitialLoading: true,
  isSuperAdmin: false,
  isSupport: false,
  user: null,
  login: async () => {},
  logout: async () => {},
};

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: {
    auth: initialAuthContext,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth();

  useEffect(() => {
    // Re-run every route's beforeLoad when auth or role state changes, not
    // just on sign-in/out - a claim that's revoked (or a token forcibly
    // refreshed to lose it) while the user is already sitting inside
    // /dashboard should re-trigger the isSuperAdmin/isSupport redirect guard
    // in dashboard/route.tsx, not just cause background API calls to start
    // failing with the dashboard shell still rendered.
    router.invalidate();
  }, [auth.isAuthenticated, auth.isSuperAdmin, auth.isSupport]);

  if (auth.isInitialLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center p-4">
        Loading...
      </div>
    );
  }

  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  return (
    <Providers>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </Providers>
  );
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}
